import { useEffect, useRef } from 'react';
import { useBillingAddresses } from '@/hooks/useBillingAddresses';
import { AddressInput } from '@/components/shared/AddressInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { BillingAddress } from '@jobos/shared/types/billingAddress';

interface BillingAddressPickerProps {
  clientId: string;
  managerId?: string;
  value: {
    billingAddressLabel?: string;
    billingAddressLine1?: string;
    billingAddressLine2?: string;
    billingCity?: string;
    billingState?: string;
    billingZip?: string;
  };
  onChange: (fields: {
    billingAddressLabel?: string;
    billingAddressLine1?: string;
    billingAddressLine2?: string;
    billingCity?: string;
    billingState?: string;
    billingZip?: string;
  }) => void;
  disabled?: boolean;
}

function formatAddress(addr: BillingAddress): string {
  const parts = [addr.addressLine1, addr.addressLine2, addr.city, addr.state, addr.zip].filter(Boolean);
  return parts.join(', ');
}

function applyAddress(addr: BillingAddress) {
  return {
    billingAddressLabel: addr.label || '',
    billingAddressLine1: addr.addressLine1 || '',
    billingAddressLine2: addr.addressLine2 || '',
    billingCity: addr.city || '',
    billingState: addr.state || '',
    billingZip: addr.zip || '',
  };
}

function isAddressEmpty(value: BillingAddressPickerProps['value']): boolean {
  return (
    !value.billingAddressLabel &&
    !value.billingAddressLine1 &&
    !value.billingAddressLine2 &&
    !value.billingCity &&
    !value.billingState &&
    !value.billingZip
  );
}

export function BillingAddressPicker({
  clientId,
  managerId,
  value,
  onChange,
  disabled = false,
}: BillingAddressPickerProps) {
  const { data: addresses } = useBillingAddresses(clientId || undefined);
  const prevClientIdRef = useRef(clientId);
  const prevManagerIdRef = useRef(managerId);

  // Auto-fill cascade when clientId or managerId changes
  useEffect(() => {
    if (!addresses || !clientId) return;

    const clientChanged = prevClientIdRef.current !== clientId;
    const managerChanged = prevManagerIdRef.current !== managerId;

    prevClientIdRef.current = clientId;
    prevManagerIdRef.current = managerId;

    if (!clientChanged && !managerChanged) return;
    if (!isAddressEmpty(value)) return;

    // Priority 1: Manager-specific default
    if (managerId) {
      const managerDefault = addresses.find(
        (a) => a.managerId === managerId && a.isDefault
      );
      if (managerDefault) {
        onChange(applyAddress(managerDefault));
        return;
      }
    }

    // Priority 2: Client-level default (managerId is null)
    const clientDefault = addresses.find(
      (a) => !a.managerId && a.isDefault
    );
    if (clientDefault) {
      onChange(applyAddress(clientDefault));
    }
  }, [clientId, managerId, addresses]);

  // Find which address is currently selected (match by fields)
  const selectedAddressId = addresses?.find(
    (a) =>
      (a.addressLine1 || '') === (value.billingAddressLine1 || '') &&
      (a.addressLine2 || '') === (value.billingAddressLine2 || '') &&
      (a.city || '') === (value.billingCity || '') &&
      (a.state || '') === (value.billingState || '') &&
      (a.zip || '') === (value.billingZip || '')
  )?.id;

  const handleSelectAddress = (addressId: string) => {
    if (addressId === 'custom') {
      // Keep current address fields, just deselect from dropdown
      return;
    }
    const addr = addresses?.find((a) => a.id === addressId);
    if (addr) {
      onChange(applyAddress(addr));
    }
  };

  return (
    <div className="space-y-4">
      <Select
        value={selectedAddressId || 'custom'}
        onValueChange={handleSelectAddress}
        disabled={disabled || !addresses?.length}
      >
        <SelectTrigger>
          <SelectValue
            placeholder={
              disabled
                ? 'Select a client first'
                : !addresses?.length
                ? 'No saved addresses'
                : 'Select a billing address'
            }
          />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="custom">Custom</SelectItem>
          {addresses?.map((addr) => (
            <SelectItem key={addr.id} value={addr.id}>
              <div className="flex flex-col">
                <span className="font-medium">
                  {addr.label || 'Untitled'}
                  {addr.isDefault && !addr.managerId && ' (Default)'}
                </span>
                <span className="text-xs text-gray-500">
                  {formatAddress(addr)}
                </span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <AddressInput
        value={{
          addressLine1: value.billingAddressLine1,
          addressLine2: value.billingAddressLine2,
          city: value.billingCity,
          state: value.billingState,
          zip: value.billingZip,
        }}
        onChange={(addr) => {
          onChange({
            ...value,
            billingAddressLine1: addr.addressLine1,
            billingAddressLine2: addr.addressLine2,
            billingCity: addr.city,
            billingState: addr.state,
            billingZip: addr.zip,
          });
        }}
        disabled={disabled}
      />
    </div>
  );
}
